import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  styled
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ChatIcon from '@mui/icons-material/Chat';
import TableChartIcon from '@mui/icons-material/TableChart';
import * as XLSX from 'xlsx-js-style';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { getCaseNumberLabel } from '../../../Utils/ReportUtils';
import { formatDate_MM_YYYY, formatDateRangeFromString } from '../../../Utils/FormatDate';
import { buildReportWorkbook } from '../../../Utils/ExcelReport';
import { useTranslation } from 'react-i18next';
import ReportsService from '../../../Services/reports.service';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext';
import { toast } from 'react-toastify';
import Constants from '../../../assests/Constants/constants.js';

// Sleek styled button component
const SleekButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '12px',
  fontWeight: '600',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  border: 'none',
  color: '#ffffff',
  minWidth: 'auto',
  height: '36px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    marginLeft: '0px',
    '& svg': {
      fontSize: '16px',
    }
  }
}));

/* ───────────── helpers ───────────── */
const cellFmt = (v) => {
  // Handle null, undefined, or NaN values
  if (v === null || v === undefined || isNaN(v)) {
    return '0';
  }
  const formatted = beautifyNumber(v || 0);
  // Check if the formatted number contains parentheses (negative numbers)
  if (formatted.includes('(') && formatted.includes(')')) {
    return `<span style="color: #d32f2f">${formatted}</span>`;
  }
  return formatted;
};

const TrialBalanceMonthlyReportModal = ({
  open,
  onClose,
  direction,
  t,
  client,
  token,
  palCodesData,
  palDates,
  isMonthly,
  userTypeContext
}) => {
  const { i18n } = useTranslation();

  // Chat functionality
  const { handleSendMessageToChat, selectUserAndOpenChat } = useHamburgerMenu();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(12);
  const [monthlyReportHtml, setMonthlyReportHtml] = useState('');
  const [isGeneratingMonthlyReport, setIsGeneratingMonthlyReport] = useState(false);
  const [showWithZeros, setShowWithZeros] = useState(false);
  const [detailedTransactions, setDetailedTransactions] = useState(false);
  const [viewAccountCard, setViewAccountCard] = useState(true);
  const [cachedMonthlyData, setCachedMonthlyData] = useState(null);

  const handleGenerate = async () => {
    const reportData = {
      year: selectedYear,
      startMonth: startMonth,
      endMonth: endMonth
    };

    try {
      setIsGeneratingMonthlyReport(true);

      // Fetch data for the selected year
      const monthlyData = await fetchMonthlyData(reportData);

      // Cache the data for potential regeneration
      setCachedMonthlyData(monthlyData);

      // Generate the monthly report HTML with the fetched data
      generateMonthlyReportHtml(reportData, monthlyData);

      // Call the parent handler
    } catch (error) {
      console.error('Error generating monthly report:', error);
    } finally {
      setIsGeneratingMonthlyReport(false);
    }
  };

  // Regenerate report when showWithZeros changes
  const handleShowWithZerosChange = (checked) => {
    setShowWithZeros(checked);
    // If report is already generated, regenerate it with new filter
    if (monthlyReportHtml && cachedMonthlyData) {
      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };
      // Regenerate the HTML with the cached data and new filter
      generateMonthlyReportHtml(reportData, cachedMonthlyData, checked);
    }
  };

  // Regenerate report when detailedTransactions changes
  const handleDetailedTransactionsChange = (checked) => {
    setDetailedTransactions(checked);
    // If report is already generated, regenerate it with new filter
    if (monthlyReportHtml && cachedMonthlyData) {
      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };
      // Regenerate the HTML with the cached data
      generateMonthlyReportHtml(reportData, cachedMonthlyData);
    }
  };

  // Regenerate report when viewAccountCard changes
  const handleViewAccountCardChange = (checked) => {
    setViewAccountCard(checked);
    // If report is already generated, regenerate it with new filter
    if (monthlyReportHtml && cachedMonthlyData) {
      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };
      // Regenerate the HTML with the cached data and pass the new viewAccountCard value
      generateMonthlyReportHtmlWithViewAccountCard(reportData, cachedMonthlyData, checked);
    }
  };

  const handleClose = () => {
    setMonthlyReportHtml('');
    setCachedMonthlyData(null);
    onClose();
  };

  // Fetch monthly data from API
  const fetchMonthlyData = async (reportData) => {
    const { year, startMonth, endMonth } = reportData;

    // Create start and end dates for the selected year
    const startDate = new Date(Date.UTC(year, startMonth - 1, 1));
    const endDate = new Date(Date.UTC(year, endMonth, 0)); // Last day of end month

    try {
      // Fetch palCodes data for the selected year
      const palCodesResponse = await ReportsService.GetPalCodesDetails(
        client.caseId,
        client.accountantId,
        startDate,
        endDate,
        false, // isAnnual - false for monthly periods
        token,
        true, // onlyProcessed
        false,  // isProfitAndLoss - false for trial balance
        true  // filterByDocumentDate
      );

      if (!palCodesResponse.ok) {
        const errorText = await palCodesResponse.text();
        throw new Error(`Failed to fetch palCodes data: ${errorText}`);
      }

      const palCodesData = await palCodesResponse.json();

      // Fetch trial balance data for the selected year
      const trialBalanceResponse = await ReportsService.GetTrialBalanceDetails(
        client.caseId,
        client.accountantId,
        startDate,
        endDate,
        false, // isAnnual - false for monthly periods
        token,
        true, // onlyProcessed
        false,  // isProfitAndLoss - false for trial balance
        null, // trialBalanceFilter
        true  // filterByDocumentDate
      );

      if (!trialBalanceResponse.ok) {
        const errorText = await trialBalanceResponse.text();
        throw new Error(`Failed to fetch trial balance data: ${errorText}`);
      }

      const trialBalanceData = await trialBalanceResponse.json();

      // Extract dates from the palCodes data
      const dates = new Set();
      palCodesData.palCodes.forEach(palCode => {
        palCode.accountCards.forEach(accountCard => {
          accountCard.dateAmounts.forEach(dateAmount => {
            dates.add(dateAmount.date);
          });
        });
      });

      const sortedDates = Array.from(dates).sort((a, b) => new Date(a) - new Date(b));

      return {
        palCodesData: palCodesData.palCodes,
        palDates: sortedDates,
        trialBalanceData: trialBalanceData
      };
    } catch (error) {
      console.error('Error fetching monthly data:', error);
      throw error;
    }
  };

  // Generate monthly report HTML using fetched monthlyData
  const generateMonthlyReportHtml = (reportData, monthlyData, showWithZerosOverride = null) => {
    if (!monthlyData || !monthlyData.palCodesData || !monthlyData.palDates) {
      console.error('No monthlyData available for monthly report');
      setMonthlyReportHtml('<div style="padding: 20px; text-align: center;">No data available for the selected period.</div>');
      return;
    }

    try {
      // Use the override value if provided, otherwise use the current state
      const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;

      // Filter data based on showWithZeros setting
      const filteredPalCodesData = filterPalCodesDataForMonthlyReport(monthlyData.palCodesData, currentShowWithZeros);

      // Generate header
      const headerContent = generateMonthlyReportHeader(reportData);

      // Generate table content using the same structure as TrialBalanceTable
      const tableContent = generateMonthlyReportTable(filteredPalCodesData, reportData, monthlyData.palDates);

      // Combine content
      const fullContent = headerContent + tableContent;

      // Generate full HTML document
      const reportHtml = generateFullHtmlDocument(fullContent, false);
      setMonthlyReportHtml(reportHtml);
    } catch (error) {
      console.error('Error generating monthly report HTML:', error);
      setMonthlyReportHtml('<div style="padding: 20px; text-align: center;">Error generating report. Please try again.</div>');
    }
  };

  // Generate monthly report HTML with specific viewAccountCard value
  const generateMonthlyReportHtmlWithViewAccountCard = (reportData, monthlyData, viewAccountCardValue, showWithZerosOverride = null) => {
    if (!monthlyData || !monthlyData.palCodesData || !monthlyData.palDates) {
      console.error('No monthlyData available for monthly report');
      setMonthlyReportHtml('<div style="padding: 20px; text-align: center;">No data available for the selected period.</div>');
      return;
    }

    try {
      // Use the override value if provided, otherwise use the current state
      const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;

      // Filter data based on showWithZeros setting
      const filteredPalCodesData = filterPalCodesDataForMonthlyReport(monthlyData.palCodesData, currentShowWithZeros);

      // Generate header
      const headerContent = generateMonthlyReportHeader(reportData);

      // Generate table content using the same structure as TrialBalanceTable with specific viewAccountCard value
      const tableContent = generateMonthlyReportTableWithViewAccountCard(filteredPalCodesData, reportData, monthlyData.palDates, viewAccountCardValue);

      // Combine content
      const fullContent = headerContent + tableContent;

      // Generate full HTML document
      const reportHtml = generateFullHtmlDocument(fullContent, false);
      setMonthlyReportHtml(reportHtml);
    } catch (error) {
      console.error('Error generating monthly report HTML:', error);
      setMonthlyReportHtml('<div style="padding: 20px; text-align: center;">Error generating report. Please try again.</div>');
    }
  };

  // Filter palCodesData based on showWithZeros setting (similar to TrialBalanceTable logic)
  const filterPalCodesDataForMonthlyReport = (palCodesData, showWithZerosOverride = null) => {
    if (!palCodesData || !palCodesData.length) {
      return [];
    }

    // Use the override value if provided, otherwise use the current state
    const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;

    let filteredData = palCodesData;

    // Apply showWithZeros filter (same logic as TrialBalanceTable)
    if (!currentShowWithZeros) {
      filteredData = filteredData.filter(palCode => {
        return palCode.accountCards.some(accountCard =>
          accountCard.dateAmounts.some(dateAmount => dateAmount.amount !== 0)
        );
      });
    }

    return filteredData;
  };



  const buildMonthlyTableColGroup = (dateHeaders) => {
    const monthCols = dateHeaders.map(() => '<col class="col-month" />').join('');
    return `<colgroup><col class="col-description" />${monthCols}<col class="col-total" /></colgroup>`;
  };

  const buildFooterSection = (dateHeaders, footerTotals, isForPrint) => {
    const footerRow = `
              <tr class="tr-footer">
                <td class="td-footer"><strong>${t('Total')}</strong></td>
                ${dateHeaders.map(date =>
      `<td class="td-footer"><strong>${cellFmt(footerTotals.dateTotals[date] || 0)}</strong></td>`
    ).join('')}
                <td class="td-footer word-wrap"><strong>${cellFmt(footerTotals.grandTotal)}</strong></td>
              </tr>`;

    if (isForPrint) {
      return `
        <div class="footer-container" style="margin-top: 40px;">
          <table class="table footer-table" dir="rtl">
            <tbody>
              ${footerRow}
            </tbody>
          </table>
        </div>
      `;
    }

    return `
      <div class="footer-container footer-container-screen">
        <table class="table footer-table" dir="rtl">
          ${buildMonthlyTableColGroup(dateHeaders)}
          <tbody>
            ${footerRow}
          </tbody>
        </table>
      </div>
    `;
  };

  const formatDateForHeader = (date) => {
    if (!date) return '';
    if (date === 'InitialBalance') return t('InitialBalance');

    if (isMonthly) {
      return formatDate_MM_YYYY(date);
    }

    // Yearly ledger view: dates may be a year ("2026"), a range ("start $ end"), or monthly keys ("2026-01")
    if (/^\d{4}$/.test(date) || date.includes(' $ ')) {
      return formatDateRangeFromString(date);
    }

    return formatDate_MM_YYYY(date);
  };

  const createMonthlyReportDateTime = () => {
    const now = new Date();
    const currentTime = now.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear().toString();
    const currentDate = `${day}/${month}/${year}`;

    return { currentDate, currentTime };
  };

  const generateMonthlyReportHeader = (reportData) => {
    const { year, startMonth, endMonth } = reportData;
    const { currentDate, currentTime } = createMonthlyReportDateTime();
    const clientName = client?.name || client?.clientName || '';
    const clientCase = client?.caseId || client?.vatClientNumber || '';
    const paddedStartMonth = String(startMonth).padStart(2, '0');
    const paddedEndMonth = String(endMonth).padStart(2, '0');

    return `
      <div class="report-page-header pageStyle">
        <div class="report-header-row">
          <div class="report-header-title-block">
            <h1 class="report-title">דוח מאזן בוחן חודשי - לפי חודשים</h1>
            <p class="report-date-range">מחודש ${paddedStartMonth}/${year} עד חודש ${paddedEndMonth}/${year}</p>
          </div>
          <div class="report-header-client-block">
            <p class="report-client-name">${clientName}</p>
            <p class="report-client-case">${getCaseNumberLabel(client?.caseType)}: ${clientCase}</p>
            <p class="report-print-datetime">תאריך הדפסה: ${currentTime} - ${currentDate}</p>
          </div>
        </div>
        <hr class="report-header-divider" />
      </div>
    `;
  };

  const buildPrintPageContinuation = (reportData, generateTableHeaders) => `
          </tbody>
          </table>
          <div class="page-break"></div>
          ${generateMonthlyReportHeader(reportData)}
          <div class="table-spacing"></div>
          <table class="table" dir="rtl">
            <thead>
              ${generateTableHeaders()}
            </thead>
            <tbody>
        `;

  // Generate the main table for monthly report - using same structure as TrialBalanceTable
  const generateMonthlyReportTable = (filteredPalCodesData, reportData, palDates, isForPrint = false) => {
    if (!filteredPalCodesData || !filteredPalCodesData.length) {
      return `
        <table class="table" dir="rtl">
          <tbody>
            <tr>
              <td style="text-align: center; padding: 20px;">No data available for the selected period.</td>
            </tr>
          </tbody>
        </table>
      `;
    }

    // Use the provided palDates parameter
    const dateHeaders = palDates || [];

    // Generate table headers HTML
    const generateTableHeaders = () => {
      return `
        <tr class="table-header-row">
          <th class="td-header">${t('Description')}</th>
          ${dateHeaders.map(date =>
        `<th class="td-header">${formatDateForHeader(date)}</th>`
      ).join('')}
          <th class="td-header">${t('Total')}</th>
        </tr>
      `;
    };

    // Generate table content using the same logic as TrialBalanceTable
    let tableContent = `
      <table class="table" dir="rtl">
        ${!isForPrint ? buildMonthlyTableColGroup(dateHeaders) : ''}
        <thead>
          ${generateTableHeaders()}
        </thead>
        <tbody>
    `;

    // Process each PAL code (same logic as TrialBalanceTable Row component)
    let rowCount = 0;
    const rowsPerPage = 18; // Adjust this number based on your page size

    filteredPalCodesData.forEach((palCode, index) => {
      // Calculate PAL code totals for each date (same as TrialBalanceTable)
      const palCodeTotals = dateHeaders.map(date => {
        return palCode.accountCards.reduce((sum, accountCard) => {
          const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
          return sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0);
        }, 0);
      });

      // Calculate PAL code grand total (same as TrialBalanceTable)
      const palCodeTotal = palCode.accountCards.reduce((sum, accountCard) => {
        return sum + accountCard.dateAmounts.reduce(
          (acc, dateAmount) => acc + (dateAmount && dateAmount.amount ? dateAmount.amount : 0),
          0
        );
      }, 0);

      // Check if we need a page break before this PAL code (only for print)
      if (isForPrint && rowCount > 0 && rowCount % rowsPerPage === 0) {
        tableContent += buildPrintPageContinuation(reportData, generateTableHeaders);
      }

      // Add PAL code row (parent row) - always visible
      tableContent += `
        <tr class="tr-palcode-parent">
          <td class="td-description">
            <strong>${palCode.palCodeId === 'NonPalCode' ? t('NonPalCode') : (palCode.palCodeId || '')}</strong>
          </td>
          ${palCodeTotals.map(total =>
        `<td class="td-amount"><strong>${cellFmt(total)}</strong></td>`
      ).join('')}
          <td class="td-total"><strong>${cellFmt(palCodeTotal)}</strong></td>
        </tr>
      `;
      rowCount++;

      // Add account cards rows (child rows) - when viewAccountCard is true
      if (viewAccountCard) {
        palCode.accountCards.forEach((accountCard, cardIndex) => {
          // Check if we need a page break before this account card (only for print)
          if (isForPrint && rowCount > 0 && rowCount % rowsPerPage === 0) {
            tableContent += buildPrintPageContinuation(reportData, generateTableHeaders);
          }

          // Calculate account card totals for each date (same as TrialBalanceTable)
          const accountCardTotals = dateHeaders.map(date => {
            const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
            return dateAmount && dateAmount.amount ? dateAmount.amount : 0;
          });

          // Calculate account card grand total (same as TrialBalanceTable)
          const accountCardTotal = accountCard.dateAmounts.reduce(
            (sum, dateAmount) => sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0),
            0
          );

          tableContent += `
            <tr class="tr-account-card">
              <td class="td-description-child">
                ${accountCard.name || ''}
              </td>
              ${accountCardTotals.map(total =>
            `<td class="td-amount">${cellFmt(total)}</td>`
          ).join('')}
              <td class="td-total">${cellFmt(accountCardTotal)}</td>
            </tr>
          `;
          rowCount++;
        });
      }
    });

    // Close the current table
    tableContent += `
      </tbody>
      </table>
    `;

    const footerTotals = calculateFooterTotals(filteredPalCodesData, dateHeaders);
    tableContent += buildFooterSection(dateHeaders, footerTotals, isForPrint);

    return tableContent;
  };

  // Generate the main table for monthly report with specific viewAccountCard value
  const generateMonthlyReportTableWithViewAccountCard = (filteredPalCodesData, reportData, palDates, viewAccountCardValue, isForPrint = false) => {
    if (!filteredPalCodesData || !filteredPalCodesData.length) {
      return `
        <table class="table" dir="rtl">
          <tbody>
            <tr>
              <td style="text-align: center; padding: 20px;">No data available for the selected period.</td>
            </tr>
          </tbody>
        </table>
      `;
    }

    // Use the provided palDates parameter
    const dateHeaders = palDates || [];

    // Generate table headers HTML
    const generateTableHeaders = () => {
      return `
        <tr class="table-header-row">
          <th class="td-header">${t('Description')}</th>
          ${dateHeaders.map(date =>
        `<th class="td-header">${formatDateForHeader(date)}</th>`
      ).join('')}
          <th class="td-header">${t('Total')}</th>
        </tr>
      `;
    };

    // Generate table content using the same logic as TrialBalanceTable
    let tableContent = `
      <table class="table" dir="rtl">
        ${!isForPrint ? buildMonthlyTableColGroup(dateHeaders) : ''}
        <thead>
          ${generateTableHeaders()}
        </thead>
        <tbody>
    `;

    // Process each PAL code (same logic as TrialBalanceTable Row component)
    let rowCount = 0;
    const rowsPerPage = 15; // Adjust this number based on your page size

    filteredPalCodesData.forEach((palCode, index) => {
      // Calculate PAL code totals for each date (same as TrialBalanceTable)
      const palCodeTotals = dateHeaders.map(date => {
        return palCode.accountCards.reduce((sum, accountCard) => {
          const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
          return sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0);
        }, 0);
      });

      // Calculate PAL code grand total (same as TrialBalanceTable)
      const palCodeTotal = palCode.accountCards.reduce((sum, accountCard) => {
        return sum + accountCard.dateAmounts.reduce(
          (acc, dateAmount) => acc + (dateAmount && dateAmount.amount ? dateAmount.amount : 0),
          0
        );
      }, 0);

      // Check if we need a page break before this PAL code (only for print)
      if (isForPrint && rowCount > 0 && rowCount % rowsPerPage === 0) {
        tableContent += buildPrintPageContinuation(reportData, generateTableHeaders);
      }

      // Add PAL code row (parent row) - always visible
      tableContent += `
        <tr class="tr-palcode-parent">
          <td class="td-description">
            <strong>${palCode.palCodeId === 'NonPalCode' ? t('NonPalCode') : (palCode.palCodeId || '')}</strong>
          </td>
          ${palCodeTotals.map(total =>
        `<td class="td-amount"><strong>${cellFmt(total)}</strong></td>`
      ).join('')}
          <td class="td-total word-wrap"><strong>${cellFmt(palCodeTotal)}</strong></td>
        </tr>
      `;
      rowCount++;

      // Add account cards rows (child rows) - when viewAccountCardValue is true
      if (viewAccountCardValue) {
        palCode.accountCards.forEach((accountCard, cardIndex) => {
          // Check if we need a page break before this account card (only for print)
          if (isForPrint && rowCount > 0 && rowCount % rowsPerPage === 0) {
            tableContent += buildPrintPageContinuation(reportData, generateTableHeaders);
          }

          // Calculate account card totals for each date (same as TrialBalanceTable)
          const accountCardTotals = dateHeaders.map(date => {
            const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
            return dateAmount && dateAmount.amount ? dateAmount.amount : 0;
          });

          // Calculate account card grand total (same as TrialBalanceTable)
          const accountCardTotal = accountCard.dateAmounts.reduce(
            (sum, dateAmount) => sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0),
            0
          );

          tableContent += `
            <tr class="tr-account-card">
              <td class="td-description-child">
                ${accountCard.name || ''}
              </td>
              ${accountCardTotals.map(total =>
            `<td class="td-amount">${cellFmt(total)}</td>`
          ).join('')}
              <td class="td-total word-wrap">${cellFmt(accountCardTotal)}</td>
            </tr>
          `;
          rowCount++;
        });
      }
    });

    // Close the current table
    tableContent += `
      </tbody>
      </table>
    `;

    const footerTotals = calculateFooterTotals(filteredPalCodesData, dateHeaders);
    tableContent += buildFooterSection(dateHeaders, footerTotals, isForPrint);

    return tableContent;
  };

  // Calculate footer totals like TrialBalanceTable
  const calculateFooterTotals = (palCodesData, dateHeaders) => {
    const totals = {
      grandTotal: 0,
      dateTotals: dateHeaders.reduce((acc, date) => {
        acc[date] = 0;
        return acc;
      }, {})
    };

    palCodesData.forEach(palCode => {
      const palCodeTotal = palCode.accountCards.reduce((sum, accountCard) => {
        return sum + accountCard.dateAmounts.reduce((acc, dateAmount) => acc + (dateAmount && dateAmount.amount ? dateAmount.amount : 0), 0);
      }, 0);

      totals.grandTotal += palCodeTotal;

      dateHeaders.forEach(date => {
        const dateTotal = palCode.accountCards.reduce((sum, accountCard) => {
          const dateAmount = accountCard.dateAmounts.find(da => da.date === date);
          return sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0);
        }, 0);
        totals.dateTotals[date] += dateTotal;
      });
    });

    return totals;
  };

  // Generate print version with page breaks
  const generatePrintVersion = (reportData, monthlyData, showWithZerosOverride = null, viewAccountCardOverride = null) => {
    if (!monthlyData || !monthlyData.palCodesData || !monthlyData.palDates) return '';

    // Use the override value if provided, otherwise use the current state
    const currentShowWithZeros = showWithZerosOverride !== null ? showWithZerosOverride : showWithZeros;
    const currentViewAccountCard = viewAccountCardOverride !== null ? viewAccountCardOverride : viewAccountCard;

    // Filter data based on showWithZeros setting
    const filteredPalCodesData = filterPalCodesDataForMonthlyReport(monthlyData.palCodesData, currentShowWithZeros);

    // Generate header
    const headerContent = generateMonthlyReportHeader(reportData);

    // Generate table content with page breaks for print using the appropriate function
    const tableContent = currentViewAccountCard
      ? generateMonthlyReportTableWithViewAccountCard(filteredPalCodesData, reportData, monthlyData.palDates, currentViewAccountCard, true)
      : generateMonthlyReportTable(filteredPalCodesData, reportData, monthlyData.palDates, true);

    // Combine content
    const fullContent = headerContent + tableContent;

    // Generate full HTML document
    return generateFullHtmlDocument(fullContent, true);
  };

  // Generate full HTML document with styles
  const generateFullHtmlDocument = (content, forPrint = false) => {
    return `
      <!DOCTYPE html>
      <html lang="he">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
          ${getReportStyles(forPrint)}
          </style>
      </head>
      <body class="trial-balance-monthly-report-modal${forPrint ? ' monthly-report-print' : ' monthly-report-display'}">
          <div class="content-wrapper">
              ${content}
          </div>
      </body>
      </html>
    `;
  };

  // Handle Excel download (same style/logic as TrialBalanceReportHtml / Monthly P&L export)
  const handleDownloadExcel = () => {
    try {
      if (!cachedMonthlyData) {
        console.error('No cached data available for Excel generation');
        return;
      }

      const filteredPalCodesData = filterPalCodesDataForMonthlyReport(
        cachedMonthlyData.palCodesData,
        showWithZeros
      );
      const dateHeaders = cachedMonthlyData.palDates || [];

      if (!filteredPalCodesData.length || !dateHeaders.length) {
        console.error('No data available for Excel generation');
        return;
      }

      const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

      const getPalCodeMonthlyTotals = (palCode) =>
        dateHeaders.map((date) =>
          palCode.accountCards.reduce((sum, accountCard) => {
            const dateAmount = accountCard.dateAmounts.find((da) => da.date === date);
            return sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0);
          }, 0)
        );

      const getPalCodeTotal = (palCode) =>
        palCode.accountCards.reduce(
          (sum, accountCard) =>
            sum +
            accountCard.dateAmounts.reduce(
              (acc, dateAmount) => acc + (dateAmount && dateAmount.amount ? dateAmount.amount : 0),
              0
            ),
          0
        );

      const header = [
        t('Description'),
        ...dateHeaders.map((date) => formatDateForHeader(date)),
        t('Total'),
      ];
      const rows = [];

      filteredPalCodesData.forEach((palCode) => {
        const palCodeLabel =
          palCode.palCodeId === 'NonPalCode' ? t('NonPalCode') : (palCode.palCodeId || '');
        rows.push([
          palCodeLabel,
          ...getPalCodeMonthlyTotals(palCode).map(round2),
          round2(getPalCodeTotal(palCode)),
        ]);

        if (viewAccountCard) {
          palCode.accountCards.forEach((accountCard) => {
            const cardMonthlyTotals = dateHeaders.map((date) => {
              const dateAmount = accountCard.dateAmounts.find((da) => da.date === date);
              return dateAmount && dateAmount.amount ? dateAmount.amount : 0;
            });
            const accountCardTotal = accountCard.dateAmounts.reduce(
              (sum, dateAmount) => sum + (dateAmount && dateAmount.amount ? dateAmount.amount : 0),
              0
            );
            rows.push([
              accountCard.name || '',
              ...cardMonthlyTotals.map(round2),
              round2(accountCardTotal),
            ]);
          });
        }
      });

      const footerTotals = calculateFooterTotals(filteredPalCodesData, dateHeaders);
      const totalRowIndex = rows.length;
      rows.push([
        t('Total'),
        ...dateHeaders.map((date) => round2(footerTotals.dateTotals[date] || 0)),
        round2(footerTotals.grandTotal),
      ]);

      const clientName = client?.name || client?.clientName || 'Report';
      const clientCase = client?.caseId || client?.vatClientNumber || '';
      const paddedStartMonth = String(startMonth).padStart(2, '0');
      const paddedEndMonth = String(endMonth).padStart(2, '0');

      const numericColIndexes = header.map((_, idx) => idx).filter((idx) => idx > 0);
      const colWidths = header.map((_, idx) =>
        (idx === 0 ? 34 : idx === header.length - 1 ? 16 : 14)
      );

      const wb = buildReportWorkbook({
        sheetName: 'Trial Balance Monthly',
        titleLines: [
          clientName,
          'דוח מאזן בוחן חודשי - לפי חודשים',
          `מחודש ${paddedStartMonth}/${selectedYear} עד חודש ${paddedEndMonth}/${selectedYear}`,
        ].filter(Boolean),
        header,
        rows,
        colWidths,
        numericColIndexes,
        totalRowIndexes: [totalRowIndex],
      });

      XLSX.writeFile(
        wb,
        `${clientName}-${clientCase}_TrialBalanceMonthlyReport.xlsx`
      );
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    try {
      if (!cachedMonthlyData) {
        console.error('No cached data available for PDF generation');
        return;
      }

      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };

      // Generate the print version HTML for PDF
      const printHtml = generatePrintVersion(reportData, cachedMonthlyData, showWithZeros, viewAccountCard);

      // Clean the HTML for PDF generation (remove interactive elements)
      const cleanedHtml = printHtml
        .replace(/border: 1px solid #ccc;/g, '')
        .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
        .replace(/\s*class="hoverTr"/g, '')
        .replace(/\s*hoverTr\s*/g, '');

      const { blob, fileName } = await ReportsService.convertHtmlToPdf(client?.caseId, cleanedHtml, token);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  // Chat functionality
  const handleSendToChat = async () => {
    try {
      if (!cachedMonthlyData) {
        console.error('No cached data available for chat');
        return;
      }

      // First, open chat with the client
      await selectUserAndOpenChat(client.caseId, client.name || 'Client');

      const reportData = {
        year: selectedYear,
        startMonth: startMonth,
        endMonth: endMonth
      };

      // Generate the print version HTML for PDF (same as handleDownloadPdf)
      const printHtml = generatePrintVersion(reportData, cachedMonthlyData, showWithZeros, viewAccountCard);

      // Clean the HTML for PDF generation (remove interactive elements)
      const cleanedHtml = printHtml
        .replace(/border: 1px solid #ccc;/g, '')
        .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
        .replace(/\s*class="hoverTr"/g, '')
        .replace(/\s*hoverTr\s*/g, '');

      const { blob, fileName } = await ReportsService.convertHtmlToPdf(client?.caseId, cleanedHtml, token);

      // Create a file from the blob
      const file = new File([blob], fileName || `${client.name}-${client.caseId}_TrialBalanceMonthlyReport.pdf`, { type: 'application/pdf' });

      // Send the file to chat
      await handleSendMessageToChat(file, client.caseId, client.name);
      toast.success(t('FileSentToChat'));
    } catch (error) {
      console.error('Error sending monthly report to chat:', error);
      toast.error(t('ErrorSendingToChat'));
    }
  };

  // Get CSS styles for the monthly report
  const getReportStyles = (forPrint = false) => {
    return `
      /* Trial Balance Monthly Report Modal Styles */
      * {
        box-sizing: border-box !important;
        font-family: Arial, sans-serif !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        font-family: Arial, sans-serif !important;
        direction: rtl !important;
      }
      
      .trial-balance-monthly-report-modal * {
        box-sizing: border-box !important;
        font-family: Arial, sans-serif !important;
      }
      
      .trial-balance-monthly-report-modal .content-wrapper {
        border: 2px solid #0066cc !important;
        border-radius: 8px !important;
        padding: 10px !important;
        margin: 0 !important;
      }

      .trial-balance-monthly-report-modal .pageStyle {
        display: block;
        background: #ffffff;
        margin: 0 0 8px 0;
        page-break-inside: avoid;
      }

      .trial-balance-monthly-report-modal .report-page-header {
        margin-bottom: 4px;
      }

      .trial-balance-monthly-report-modal .report-header-row {
        display: flex !important;
        flex-direction: row !important;
        direction: rtl !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        gap: 16px !important;
        padding: 4px 0 !important;
        width: 100% !important;
      }

      .trial-balance-monthly-report-modal .report-header-title-block {
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        direction: rtl !important;
        text-align: right !important;
        flex: 1 !important;
      }

      .trial-balance-monthly-report-modal .report-title {
        margin: 0 0 6px 0 !important;
        padding: 0 !important;
        font-size: 24px !important;
        font-weight: 700 !important;
        color: #000 !important;
        line-height: 1.2 !important;
        text-decoration: none !important;
        text-align: right !important;
        width: 100% !important;
      }

      .trial-balance-monthly-report-modal .report-date-range {
        margin: 0 !important;
        padding: 0 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #222 !important;
        text-decoration: none !important;
        text-align: right !important;
        width: 100% !important;
      }

      .trial-balance-monthly-report-modal .report-header-client-block {
        text-align: left !important;
        direction: rtl !important;
        flex: 1 !important;
        max-width: 45% !important;
        align-self: flex-start !important;
      }

      .trial-balance-monthly-report-modal .report-client-name {
        margin: 0 0 4px 0 !important;
        padding: 0 !important;
        font-size: 15px !important;
        font-weight: 700 !important;
        color: #000 !important;
        text-align: left !important;
      }

      .trial-balance-monthly-report-modal .report-client-case,
      .trial-balance-monthly-report-modal .report-print-datetime {
        margin: 0 0 2px 0 !important;
        padding: 0 !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #333 !important;
        text-align: left !important;
      }

      .trial-balance-monthly-report-modal .report-header-divider {
        border: none !important;
        border-top: 2px solid #000 !important;
        margin: 4px 0 8px !important;
        width: 100% !important;
      }

      .trial-balance-monthly-report-modal .table-spacing {
        height: 8px;
      }
      
      .trial-balance-monthly-report-modal .table {
        width: 100% !important;
        border-collapse: collapse !important;
        direction: rtl !important;
      }
      
      .trial-balance-monthly-report-modal .table th {
        background-color: rgb(217, 221, 249) !important;
        color: #304FFF !important;
        font-weight: bold !important;
        padding: 10px 8px !important;
        border: 1px solid #ddd !important;
        text-align: right !important;
        font-size: 13px !important;
      }
      
      .trial-balance-monthly-report-modal .table td {
        border: 1px solid #ddd !important;
        padding: 4px !important;
        text-align: right !important;
        font-size: 12px !important;
      }
      
      .trial-balance-monthly-report-modal .td-header {
        background-color: rgb(217, 221, 249) !important;
        color: #304FFF !important;
        font-weight: bold !important;
        padding: 10px 8px !important;
        border: 1px solid #ddd !important;
        font-size: 14px !important;
        text-align: right !important;
      }
      
      .trial-balance-monthly-report-modal .td-description {
        text-align: right !important;
        padding: 8px 8px !important;
        margin-right: 5px !important;
        border: 0.5px solid #e0e0e0 !important;
        font-weight: 300 !important;
        word-wrap: break-word !important;
        width: 220px !important;
      }
      
      .trial-balance-monthly-report-modal .td-description-child {
        text-align: right !important;
        padding: 8px 8px 8px 24px !important;
        margin-right: 5px !important;
        border: 0.5px solid #e0e0e0 !important;
        font-weight: 300 !important;
        background-color: #ffffff !important;
      }
      
      .trial-balance-monthly-report-modal .tr-palcode-parent {
        background-color: #f7f7f7 !important;
        border-bottom: 2px solid #000 !important;
        font-weight: 300 !important;
      }
      
      .trial-balance-monthly-report-modal .tr-palcode-parent:hover {
        background-color: rgba(0, 0, 0, 0.04) !important;
      }
      
      .trial-balance-monthly-report-modal .tr-account-card {
        background-color: #ffffff !important;
      }
      
      .trial-balance-monthly-report-modal .tr-account-card:hover {
        background-color: rgba(0, 0, 0, 0.02) !important;
      }
      
      .trial-balance-monthly-report-modal .tr-footer {
        background: #e8e8e8 !important;
        border-top: 2px solid #000 !important;
      }
      
      .trial-balance-monthly-report-modal .td-footer {
        background: rgb(251, 251, 251) !important;
        text-align: right !important;
        margin-left: 5px !important;
        word-wrap: break-word !important;
        font-weight: bold !important;
        padding: 10px 6px !important;
        border: 1px solid #ddd !important;
        font-size: 12px !important;
      }
      
      .trial-balance-monthly-report-modal .td-amount {
        text-align: right !important;
        padding: 8px 4px !important;
        font-family: Arial, sans-serif !important;
        font-size: 10px !important;
        font-weight: 400 !important;
      }
      
      .trial-balance-monthly-report-modal .td-total {
        font-weight: bold !important;
        text-align: right !important;
        padding: 8px 4px !important;
        font-family: Arial, sans-serif !important;
        font-size: 10px !important;
      }
      
      /* Add visual separation between PAL code groups */
      .trial-balance-monthly-report-modal .tr-palcode-parent + .tr-account-card:first-of-type {
        border-top: 1px solid #ccc !important;
      }
      
      /* Ensure span styles work */
      .trial-balance-monthly-report-modal span {
        color: inherit !important;
      }
      
      .trial-balance-monthly-report-modal span[style*="color: #d32f2f"] {
        color: #d32f2f !important;
        font-weight: bold !important;
      }

      ${forPrint ? '' : `
      @media screen {
        .monthly-report-display .table,
        .monthly-report-display .footer-table {
          table-layout: fixed !important;
          width: 100% !important;
        }

        .monthly-report-display col.col-description {
          width: 220px !important;
        }

        .monthly-report-display col.col-total {
          width: 90px !important;
        }

        .monthly-report-display .footer-table .td-footer:first-child {
          width: 220px !important;
          min-width: 220px !important;
          max-width: 220px !important;
        }

        .monthly-report-display .footer-table .td-footer {
          margin-left: 0 !important;
        }

        .monthly-report-display .tr-footer {
          position: sticky !important;
          bottom: 0 !important;
        }
      }
      `}
      
      @media print {
          @page {
            /* Pick one: A4/Letter, both shown */
            size: A4 landscape;         /* for A4 */
            /* size: Letter landscape;  /* for US Letter */
            margin: 10mm 4mm 4mm 4mm;
          }

          .word-wrap{
            word-wrap: break-word !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: unset !important;
          }
          
          /* Ensure table structure is maintained for printing */
          .trial-balance-monthly-report-modal .table {
            table-layout: fixed !important;
            width: 100% !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
            margin-top: 10px !important;
            max-width: 100% !important;
          }


          
          /* Set specific column widths for print */
          .trial-balance-monthly-report-modal .table th:first-child,
          .trial-balance-monthly-report-modal .table td:first-child {
            width: 95px !important; /* Description column - further reduced */
            min-width: 95px !important;
            max-width: 95px !important;
          }
          
          .trial-balance-monthly-report-modal .table th:not(:first-child):not(:last-child),
          .trial-balance-monthly-report-modal .table td:not(:first-child):not(:last-child) {
            width: 70px !important; /* Month columns - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          .trial-balance-monthly-report-modal .table th:last-child,
          .trial-balance-monthly-report-modal .table td:last-child {
            width: 70px !important; /* Total column - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          /* Force table to maintain structure */
          .trial-balance-monthly-report-modal .table,
          .trial-balance-monthly-report-modal .table * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          @page {
              @top-left {
                  content: "עמוד " counter(page) " מתוך " counter(pages);
                  font-size: 10px;
                  font-weight: 800;
                  color: #333;
                  margin-left: 5px;
              }
          }
          
          .page-break {
              page-break-before: always !important;
              break-before: page !important;
          }

          .trial-balance-monthly-report-modal .report-page-header {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 6px !important;
          }

          .trial-balance-monthly-report-modal .report-title {
            font-size: 20px !important;
          }

          .trial-balance-monthly-report-modal .report-date-range {
            font-size: 12px !important;
          }

          .trial-balance-monthly-report-modal .report-client-name {
            font-size: 13px !important;
          }

          .trial-balance-monthly-report-modal .report-client-case,
          .trial-balance-monthly-report-modal .report-print-datetime {
            font-size: 11px !important;
          }

          .trial-balance-monthly-report-modal .report-header-divider {
            margin: 4px 0 6px !important;
          }
          
          /* Style for manual table headers */
          .trial-balance-monthly-report-modal .table-header-row {
            background-color: rgb(217, 221, 249) !important;
            color: #304FFF !important;
            font-weight: bold !important;
            text-align: center !important;
            padding: 8px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 12px !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .trial-balance-monthly-report-modal .table-header-row th {
            background-color: rgb(217, 221, 249) !important;
            color: #304FFF !important;
            font-weight: bold !important;
            text-align: center !important;
            padding: 8px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 12px !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .page-number::before {
              content: counter(page);
          }
          
          .total-pages::before {
              content: counter(pages);
          }
          
          body {
              counter-reset: page;
              
          }
      
        .trial-balance-monthly-report-modal body {
          margin: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        .trial-balance-monthly-report-modal .content-wrapper {
          border: none !important;
          padding: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        
        /* Overall page width and size controls */
        .trial-balance-monthly-report-modal {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
        
        
        /* Add spacing for page breaks to account for header */
        .trial-balance-monthly-report-modal .page-break {
          margin-top: 10px !important;
        }
        
        /* Add spacing after page breaks to ensure content starts after header */
        .trial-balance-monthly-report-modal .table-spacing {
          height: 20px !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .trial-balance-monthly-report-modal #header {
            position: fixed !important;
            top: 0px !important;
            left: 0px !important;
            padding: 2px !important;
            width: 100% !important;
            z-index: 1000 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            background-color: none !important;
          }

          /* Force table headers to repeat on each page - Maximum specificity */
          .trial-balance-monthly-report-modal .table {
            border-collapse: collapse !important;
            table-layout: fixed !important;
            width: 100% !important;
          }
          
          .trial-balance-monthly-report-modal .table thead {
            display: table-header-group !important;
            position: relative !important;
            z-index: 10 !important;
          }
          
          .trial-balance-monthly-report-modal .table thead tr {
            display: table-row !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          .trial-balance-monthly-report-modal .table thead th,
          .trial-balance-monthly-report-modal .table thead .td-header {
            display: table-cell !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            background-color: rgb(217, 221, 249) !important;
            color: #304FFF !important;
            font-weight: bold !important;
            text-align: right !important;
            padding: 8px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 12px !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            position: relative !important;
            z-index: 10 !important;
          }
          
          /* Ensure tbody doesn't interfere with thead */
          .trial-balance-monthly-report-modal .table tbody {
            display: table-row-group !important;
          }
          
          .trial-balance-monthly-report-modal .table tbody tr {
            display: table-row !important;
          }
          
          /* Additional rules to force header repetition */
          .trial-balance-monthly-report-modal .table thead {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Footer container positioning for print */
          .trial-balance-monthly-report-modal .footer-container {
            position: relative !important;
            margin-top: 20px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin-top: 30px !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table .tr-footer {
            background: #e8e8e8 !important;
            border-top: 2px solid #000 !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            position: static !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table .td-footer {
            background: rgb(251, 251, 251) !important;
            text-align: right !important;
            margin-left: 5px !important;
            font-weight: bold !important;
            padding: 8px 4px !important;
            border: 0.2px solid #e0e0e0 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Print media styles for table cells */
          .trial-balance-monthly-report-modal .td-description {
            font-weight: 200 !important;
            word-wrap: break-word !important;
            font-size: 11px !important;
            line-height: 1.1 !important;
            padding: 4px !important;
          }
          
          .trial-balance-monthly-report-modal .td-amount {
            font-size: 11px !important;
            font-weight: 400 !important;
            font-family: Arial, sans-serif !important;
            word-wrap: break-word !important;
            line-height: 1.1 !important;
            padding: 4px !important;
            text-align: right !important;
          }
          
          .trial-balance-monthly-report-modal .td-total {
            font-size: 11px !important;
            font-weight: bold !important;
            font-family: Arial, sans-serif !important;
            line-height: 1.1 !important;
            padding: 4px !important;
            word-wrap: break-word !important;
            text-align: right !important;
          }
          
          /* Specific styling for SortingCode header */
          .trial-balance-monthly-report-modal .table th:first-child {
            font-size: 12px !important;
            padding: 8px 6px !important;
            text-align: right !important;
            word-wrap: break-word !important;
          }
          
          /* Ensure footer table has proper column widths for print */
          .trial-balance-monthly-report-modal .footer-table {
            table-layout: fixed !important;
            direction: rtl !important;
          }
          
          /* Enhanced footer table styling for print */
          .trial-balance-monthly-report-modal .footer-table {
            table-layout: fixed !important;
            direction: rtl !important;
            border-collapse: collapse !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          
          /* Footer table column widths for print */
          .trial-balance-monthly-report-modal .footer-table td:first-child {
            width: 90px !important; /* Match description column - further reduced */
            min-width: 90px !important;
            max-width: 90px !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table td:not(:first-child):not(:last-child) {
            width: 70px !important; /* Match month columns - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table td:last-child {
            width: 70px !important; /* Match total column - reduced for better fit */
            min-width: 70px !important;
            max-width: 70px !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table .tr-footer {
            background: #e8e8e8 !important;
            border-top: 2px solid #000 !important;
            font-weight: bold !important;
          }
          
          .trial-balance-monthly-report-modal .footer-table .td-footer {
            background: rgb(251, 251, 251) !important;
            text-align: right !important;
            font-weight: bold !important;
            padding: 10px 6px !important;
            border: 1px solid #ddd !important;
            font-size: 12px !important;
            font-family: Arial, sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
      }
    `;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="trial-balance-monthly-report-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: monthlyReportHtml ? '90%' : 500,
        height: monthlyReportHtml ? '90%' : 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: '10px',
        p: 2,
      }}>
        {!monthlyReportHtml ? (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 1, textAlign: 'center' }}>
              דוח מאזן בוחן לפי חודשי
            </Typography>

            <Grid dir={direction} container spacing={3}>
              {/* Year Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('Year')}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>שנה</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    label="שנה"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <MenuItem dir={direction} key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Start Month */}
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('StartMonth')}
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>מחודש</InputLabel>
                  <Select
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    label="מחודש"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <MenuItem dir={direction} key={month} value={month}>
                        {new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* End Month */}
              <Grid item xs={6}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('EndMonth')}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>עד חודש</InputLabel>
                  <Select
                    value={endMonth}
                    onChange={(e) => setEndMonth(e.target.value)}
                    label="עד חודש"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <MenuItem dir={direction} key={month} value={month}>
                        {new Date(selectedYear, month - 1).toLocaleDateString('he-IL', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box dir={direction} sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ width: '120px' }}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={isGeneratingMonthlyReport}
                sx={{ width: '120px', backgroundColor: '#304FFF', '&:hover': { backgroundColor: '#304FFF' } }}
              >
                {isGeneratingMonthlyReport ? t('Loading') : t('GenerateReport')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Report Display */}
            <Box dir={direction} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>

                <SleekButton
                  onClick={handleDownloadExcel}
                  startIcon={<TableChartIcon />}
                >
                  {t('DownloadExcelFile')}
                </SleekButton>

                {/* PDF Download Button */}
                <SleekButton
                  onClick={handleDownloadPdf}
                  startIcon={<DownloadIcon />}
                >
                  {t('DownloadPDFFile')}
                </SleekButton>

                <SleekButton
                  onClick={() => {
                    if (cachedMonthlyData) {
                      const reportData = {
                        year: selectedYear,
                        startMonth: startMonth,
                        endMonth: endMonth
                      };
                      const printHtml = generatePrintVersion(reportData, cachedMonthlyData, showWithZeros, viewAccountCard);
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(printHtml);
                      printWindow.document.close();
                      printWindow.print();
                    }
                  }}
                  startIcon={<PrintIcon />}
                >
                  {t('Print')}
                </SleekButton>


                {
                  userTypeContext?.toString() !== Constants.UserType.Client &&
                  <SleekButton
                    onClick={handleSendToChat}
                    startIcon={<ChatIcon />}
                  >
                    {t('SendToChat')}
                  </SleekButton>
                }

                {/* Show With Zeros Checkbox */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: '#f8f9ff',
                  borderRadius: '8px',
                  border: '1px solid #e3e8ff',
                  height: '36px',
                }}>
                  <Checkbox
                    checked={showWithZeros}
                    onChange={(e) => handleShowWithZerosChange(e.target.checked)}
                    name="ShowWithZeros"
                    color="primary"
                    sx={{ padding: '2px' }}
                    size="small"
                  />
                  <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                    {t('ShowWithZeros')}
                  </span>
                </div>

                {/* View Account Cards Checkbox */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: '#f8f9ff',
                  borderRadius: '8px',
                  border: '1px solid #e3e8ff',
                  height: '36px',
                }}>
                  <Checkbox
                    checked={viewAccountCard}
                    onChange={(e) => handleViewAccountCardChange(e.target.checked)}
                    name="ByAccountCard"
                    color="primary"
                    sx={{ padding: '2px' }}
                    size="small"
                  />
                  <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                    {t('ByAccountCard')}
                  </span>
                </div>
              </Box>

              <SleekButton
                onClick={handleClose}
                sx={{
                  background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E55A2B 0%, #D44A1A 100%)',
                  }
                }}
              >
                {t('Close')}
              </SleekButton>
            </Box>

            {/* HTML Report Content - Using iframe to completely isolate styles */}
            <iframe
              srcDoc={monthlyReportHtml}
              style={{
                width: '100%',
                height: '70vh',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: 'white'
              }}
              title="Trial Balance Monthly Report"
            />
          </>
        )}
      </Box>
    </Modal>
  );
};

// Helper function to group array by key
const groupBy = (arr, key) => {
  return arr.reduce((acc, item) => {
    if (!acc[item[key]]) {
      acc[item[key]] = [];
    }
    acc[item[key]].push(item);
    return acc;
  }, {});
};

export default TrialBalanceMonthlyReportModal;
